import { useMemo, useRef, useState } from "react";
import { publicApi } from "@/lib/api";
import {
  X,
  MapPin,
  Camera,
  Compass,
  Check,
  Sparkles,
  Loader2,
  ArrowRight,
  Locate,
  ImageOff,
  Lightbulb,
} from "lucide-react";

/* -----------------------------------------------------------
   VoyageTempsModal — Énigme spéciale "Voyage dans le temps"
   3 couches : GPS (source de vérité) · IA vision (bonus) · Souvenir
   ----------------------------------------------------------- */

const STEPS = {
  ARCHIVE: "archive",     // Écran d'accueil : archive + "M'y rendre"
  LOCATING: "locating",   // Cherche la position GPS du joueur
  TOO_FAR: "too_far",     // Trop loin — indication de la direction
  CAPTURE: "capture",     // Sur place, ouvrir l'appareil photo
  ANALYZING: "analyzing", // Envoi backend + IA
  UNLOCKED: "unlocked",   // Avant/après + code + anecdote
};

function playUnlockSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const t0 = ctx.currentTime;
    // Effet "vieux mécanisme" plus long, avec réverbération
    const notes = [
      { f: 660, t: 0, v: 0.25 },
      { f: 880, t: 0.09, v: 0.22 },
      { f: 1180, t: 0.19, v: 0.28 },
      { f: 1480, t: 0.32, v: 0.3 },
    ];
    notes.forEach(({ f, t, v }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(f, t0 + t);
      g.gain.setValueAtTime(v, t0 + t);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + t + 0.35);
      osc.connect(g).connect(ctx.destination);
      osc.start(t0 + t);
      osc.stop(t0 + t + 0.4);
    });
  } catch { /* silent */ }
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("read fail"));
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

function bearingLabel(lat1, lng1, lat2, lng2) {
  const y = Math.sin(((lng2 - lng1) * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180);
  const x =
    Math.cos((lat1 * Math.PI) / 180) * Math.sin((lat2 * Math.PI) / 180) -
    Math.sin((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.cos(((lng2 - lng1) * Math.PI) / 180);
  const deg = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  const labels = ["Nord", "Nord-Est", "Est", "Sud-Est", "Sud", "Sud-Ouest", "Ouest", "Nord-Ouest"];
  return labels[Math.round(deg / 45) % 8];
}

export default function VoyageTempsModal({ enigme, etape, session, onClose, onSolved }) {
  const [step, setStep] = useState(STEPS.ARCHIVE);
  const [error, setError] = useState("");
  const [userPos, setUserPos] = useState(null);
  const [distance, setDistance] = useState(null);
  const [userPhoto, setUserPhoto] = useState(null); // base64
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const target = useMemo(
    () => ({
      lat: enigme.voyageLat ?? etape.lat,
      lng: enigme.voyageLng ?? etape.lng,
      rayon: enigme.rayonMetres || 30,
    }),
    [enigme, etape]
  );

  /* ── Locate the player ─────────────────────────────────── */
  function startLocate() {
    setError("");
    if (!navigator.geolocation) {
      setError("Ce navigateur ne fournit pas la géolocalisation.");
      return;
    }
    setStep(STEPS.LOCATING);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserPos({ lat: latitude, lng: longitude });
        const d = distanceMetres(latitude, longitude, target.lat, target.lng);
        setDistance(d);
        if (d <= target.rayon) {
          setStep(STEPS.CAPTURE);
        } else {
          setStep(STEPS.TOO_FAR);
        }
      },
      (err) => {
        setError(
          err.code === 1
            ? "Vous devez autoriser la géolocalisation pour cette énigme."
            : "Impossible d'obtenir votre position — vérifiez le GPS."
        );
        setStep(STEPS.ARCHIVE);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 5000 }
    );
  }

  /* ── Capture photo ─────────────────────────────────────── */
  async function handleCapture(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setError("Photo trop lourde (>6 Mo).");
      return;
    }
    setError("");
    try {
      const b64 = await fileToBase64(file);
      // Redimensionner à 1200px max côté long
      const resized = await resizeImage(b64, 1200);
      setUserPhoto(resized);
      await submitVoyage(resized);
    } catch {
      setError("Erreur lors du chargement de la photo.");
    }
  }

  async function submitVoyage(photoB64) {
    setStep(STEPS.ANALYZING);
    try {
      const { data } = await publicApi.post(
        `/sessions/by-code/${session.codeAcces}/voyage-verify`,
        {
          enigmeId: enigme.id,
          numeroCadenas: enigme.numeroCadenas,
          userLat: userPos.lat,
          userLng: userPos.lng,
          userPhotoB64: photoB64,
        }
      );
      setResult(data);
      if (data.gpsOk) {
        playUnlockSound();
        setStep(STEPS.UNLOCKED);
      } else {
        // Rare : GPS avait dérivé entre la mesure locale et l'envoi. On repropose.
        setError(
          `Vous vous êtes déplacé — vous êtes maintenant à ${Math.round(
            data.distance
          )}m du point cible.`
        );
        setStep(STEPS.TOO_FAR);
        setDistance(data.distance);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur d'enregistrement.");
      setStep(STEPS.CAPTURE);
    }
  }

  /* ── Rendu selon la phase ───────────────────────────────── */
  return (
    <div
      data-testid="voyage-modal"
      className="fixed inset-0 z-[100] bg-noir/95 backdrop-blur-md flex items-start md:items-center justify-center p-0 md:p-4 overflow-auto"
    >
      <div className="w-full max-w-2xl min-h-full md:min-h-0 md:max-h-[92vh] bg-noir-100 border border-or/25 md:rounded-sm md:overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-or/15 bg-noir-100/95 backdrop-blur">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-sm bg-or/10 border border-or/40 flex items-center justify-center flex-shrink-0">
              <Compass className="w-5 h-5 text-or" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/80">
                Étape {etape.ordre} · Cadenas #{enigme.numeroCadenas} · Voyage dans le temps
              </div>
              <h2 className="mt-1 font-display italic text-xl md:text-2xl text-or leading-tight">
                {enigme.resumeIdee || etape.nom}
              </h2>
            </div>
          </div>
          {step !== STEPS.ANALYZING && (
            <button
              onClick={onClose}
              data-testid="voyage-close-btn"
              className="p-2 text-parchemin/50 hover:text-parchemin transition"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Corps selon phase */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-5">
          {step === STEPS.ARCHIVE && (
            <ArchivePhase
              enigme={enigme}
              onStart={startLocate}
              error={error}
            />
          )}

          {step === STEPS.LOCATING && <LocatingPhase />}

          {step === STEPS.TOO_FAR && (
            <TooFarPhase
              distance={distance}
              rayon={target.rayon}
              userPos={userPos}
              target={target}
              onRetry={startLocate}
              error={error}
            />
          )}

          {step === STEPS.CAPTURE && (
            <CapturePhase
              enigme={enigme}
              distance={distance}
              onOpenCamera={() => fileInputRef.current?.click()}
              onSkipPhoto={() => submitVoyage(null)}
              error={error}
            />
          )}

          {step === STEPS.ANALYZING && <AnalyzingPhase />}

          {step === STEPS.UNLOCKED && result && (
            <UnlockedPhase
              enigme={enigme}
              userPhoto={userPhoto}
              result={result}
              onContinue={onSolved}
            />
          )}
        </div>

        {/* input caméra caché */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleCapture}
          data-testid="voyage-camera-input"
        />
      </div>
    </div>
  );
}

/* ═══════════ Sub-phases ═══════════════════════════════════ */

function ArchivePhase({ enigme, onStart, error }) {
  return (
    <>
      {/* Archive photo */}
      <figure className="relative border-2 border-or/40 rounded-sm overflow-hidden bg-noir-50">
        {enigme.archivePhotoUrl ? (
          <img
            src={enigme.archivePhotoUrl}
            alt="Archive"
            data-testid="archive-photo"
            className="w-full h-auto object-cover"
            style={{ filter: "sepia(0.4) contrast(1.1)" }}
          />
        ) : (
          <div className="aspect-[4/3] flex items-center justify-center text-parchemin/40">
            <ImageOff className="w-8 h-8" />
          </div>
        )}
        <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-noir via-noir/70 to-transparent p-3">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or">
            Archive {enigme.archiveAnnee || ""}
          </div>
        </figcaption>
      </figure>

      {/* Instruction */}
      <div className="p-4 border-l-2 border-or bg-or/5 rounded-r-sm">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-3.5 h-3.5 text-or" />
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-or">
            Le défi du temps
          </span>
        </div>
        <p className="text-parchemin italic font-display leading-relaxed">
          {enigme.question ||
            "Retrouvez l'endroit exact où cette photo a été prise. Rendez-vous sur place, et reprenez la scène aujourd'hui."}
        </p>
      </div>

      {/* Indice éventuel */}
      {enigme.indice1 && (
        <div className="flex items-start gap-3 p-3 bg-vert-900/30 border-l-2 border-vert-50 rounded-r-sm">
          <Lightbulb className="w-4 h-4 text-vert-50 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-parchemin/85 italic font-display">
            {enigme.indice1}
          </p>
        </div>
      )}

      {error && (
        <div className="p-3 rounded-sm bg-oxyde/10 border border-oxyde/40 text-sm text-parchemin/90">
          {error}
        </div>
      )}

      <button
        onClick={onStart}
        data-testid="voyage-start-btn"
        className="w-full py-4 rounded-sm bg-or hover:bg-or-100 text-noir font-medium tracking-wide inline-flex items-center justify-center gap-2 shadow-[0_14px_40px_-10px_rgba(200,146,42,0.6)] transition"
      >
        <MapPin className="w-4 h-4" />
        M&apos;y rendre
        <ArrowRight className="w-4 h-4" />
      </button>
    </>
  );
}

function LocatingPhase() {
  return (
    <div className="py-16 flex flex-col items-center gap-5 text-center">
      <div className="relative">
        <Locate className="w-12 h-12 text-or animate-glow-pulse" strokeWidth={1.5} />
      </div>
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/80">
        Recherche de position
      </div>
      <p className="text-sm text-parchemin/70 italic font-display max-w-xs">
        La boussole tourne… nous cherchons votre point exact.
      </p>
    </div>
  );
}

function TooFarPhase({ distance, rayon, userPos, target, onRetry, error }) {
  const dir = userPos && target ? bearingLabel(userPos.lat, userPos.lng, target.lat, target.lng) : "";
  const dm = distance ? Math.round(distance) : 0;
  return (
    <>
      <div className="py-8 flex flex-col items-center gap-3 text-center">
        <div className="w-16 h-16 rounded-full border-2 border-or bg-noir flex items-center justify-center">
          <Compass className="w-7 h-7 text-or" strokeWidth={1.5} />
        </div>
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or">
          Encore un peu…
        </div>
        <div className="font-display text-3xl text-parchemin stat-graved">
          {dm}<span className="text-parchemin/40 text-2xl">&nbsp;m</span>
        </div>
        <p className="text-sm text-parchemin/70 italic font-display max-w-xs">
          Vous êtes à {dm}m — dirigez-vous vers le <span className="text-or not-italic">{dir}</span>.
          Rapprochez-vous à moins de {rayon}m.
        </p>
      </div>

      {error && (
        <div className="p-3 rounded-sm bg-oxyde/10 border border-oxyde/40 text-sm text-parchemin/90 text-center">
          {error}
        </div>
      )}

      <button
        onClick={onRetry}
        data-testid="voyage-retry-locate"
        className="w-full py-3 rounded-sm border border-or/40 hover:border-or hover:bg-or/10 text-or font-mono text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 transition"
      >
        <Locate className="w-3.5 h-3.5" />
        Recalculer ma position
      </button>
    </>
  );
}

function CapturePhase({ enigme, distance, onOpenCamera, error }) {
  return (
    <>
      {enigme.archivePhotoUrl && (
        <figure className="border border-or/30 rounded-sm overflow-hidden">
          <img
            src={enigme.archivePhotoUrl}
            alt="Archive"
            className="w-full h-auto object-cover"
            style={{ filter: "sepia(0.4) contrast(1.1)" }}
          />
        </figure>
      )}

      <div className="p-4 border-l-2 border-vert-50 bg-vert-900/30 rounded-r-sm text-center">
        <Check className="w-5 h-5 text-vert-50 mx-auto mb-2" />
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-vert-50">
          Vous y êtes · {Math.round(distance || 0)}m
        </div>
        <p className="mt-2 text-sm text-parchemin/85 italic font-display">
          Alignez votre photo sur le cadrage de l&apos;archive et immortalisez le moment.
        </p>
      </div>

      {error && (
        <div className="p-3 rounded-sm bg-oxyde/10 border border-oxyde/40 text-sm text-parchemin/90">
          {error}
        </div>
      )}

      <button
        onClick={onOpenCamera}
        data-testid="voyage-camera-btn"
        className="w-full py-4 rounded-sm bg-or hover:bg-or-100 text-noir font-medium tracking-wide inline-flex items-center justify-center gap-2 shadow-[0_14px_40px_-10px_rgba(200,146,42,0.6)] transition"
      >
        <Camera className="w-4 h-4" />
        Prendre la photo aujourd&apos;hui
      </button>
    </>
  );
}

function AnalyzingPhase() {
  return (
    <div className="py-16 flex flex-col items-center gap-5 text-center">
      <Loader2 className="w-10 h-10 text-or animate-spin" strokeWidth={1.5} />
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/80">
        Le temps se replie…
      </div>
      <p className="text-sm text-parchemin/70 italic font-display max-w-xs">
        L&apos;archive et votre photo se rencontrent. Un instant, aventurier.
      </p>
    </div>
  );
}

function UnlockedPhase({ enigme, userPhoto, result, onContinue }) {
  const iaComment = result?.vision?.comment;
  const iaEnabled = result?.vision?.iaEnabled;

  return (
    <div className="space-y-5" data-testid="voyage-unlocked">
      <div className="text-center pb-2">
        <div className="mx-auto w-16 h-16 rounded-full border-2 border-or bg-noir flex items-center justify-center mb-3 animate-glow-pulse">
          <Check className="w-8 h-8 text-or" strokeWidth={2.5} />
        </div>
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/80">
          Voyage accompli
        </div>
        <h3 className="mt-2 font-display font-medium text-3xl text-parchemin">
          <span className="italic text-or">Le temps s&apos;est plié.</span>
        </h3>
      </div>

      {/* Avant / Après */}
      <div className="grid grid-cols-2 gap-2">
        <figure className="border border-or/30 rounded-sm overflow-hidden">
          {enigme.archivePhotoUrl ? (
            <img
              src={enigme.archivePhotoUrl}
              alt="Archive"
              className="w-full aspect-square object-cover"
              style={{ filter: "sepia(0.5) contrast(1.1)" }}
            />
          ) : (
            <div className="w-full aspect-square bg-noir-50" />
          )}
          <figcaption className="p-2 text-center bg-noir-100">
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-or/80">
              Hier · {enigme.archiveAnnee || "archives"}
            </span>
          </figcaption>
        </figure>
        <figure className="border border-vert-50/40 rounded-sm overflow-hidden">
          {userPhoto ? (
            <img
              src={userPhoto}
              alt="Votre photo"
              className="w-full aspect-square object-cover"
            />
          ) : (
            <div className="w-full aspect-square bg-noir-50 flex items-center justify-center text-parchemin/40">
              <ImageOff className="w-6 h-6" />
            </div>
          )}
          <figcaption className="p-2 text-center bg-vert-900/40">
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-vert-50">
              Aujourd&apos;hui · vous
            </span>
          </figcaption>
        </figure>
      </div>

      {/* Commentaire IA (bonus) */}
      {iaComment && (
        <div className="p-3 border-l-2 border-or bg-or/5 rounded-r-sm">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-3 h-3 text-or" />
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-or">
              {iaEnabled ? "Le regard de l'archiviste" : "Souvenir gardé"}
            </span>
          </div>
          <p className="text-sm text-parchemin/90 italic font-display leading-relaxed">
            « {iaComment} »
          </p>
        </div>
      )}

      {/* Code cadenas révélé */}
      <div className="p-5 bg-noir border border-or/30 rounded-sm text-center">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70 mb-3">
          Code à composer sur le cadenas
        </div>
        <div
          data-testid="voyage-revealed-code"
          className="font-mono font-bold text-5xl text-or stat-graved tracking-[0.35em]"
        >
          {enigme.codeCadenas}
        </div>
      </div>

      {/* Anecdote */}
      {enigme.explication && (
        <div className="p-4 bg-vert-900/40 border-l-2 border-vert-50 rounded-r-sm">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-vert-50" />
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-vert-50">
              Ce qui a changé…
            </span>
          </div>
          <p className="text-sm text-parchemin/90 leading-relaxed italic font-display">
            {enigme.explication}
          </p>
        </div>
      )}

      <button
        onClick={onContinue}
        data-testid="voyage-continue-btn"
        className="w-full py-3.5 bg-or hover:bg-or-100 text-noir font-medium tracking-wide rounded-sm inline-flex items-center justify-center gap-2 transition shadow-[0_10px_30px_-10px_rgba(200,146,42,0.5)]"
      >
        Poursuivre l&apos;aventure
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ═══════════ Utils ═══════════════════════════════════════ */

function distanceMetres(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dphi = ((lat2 - lat1) * Math.PI) / 180;
  const dlmb = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dphi / 2) ** 2 +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dlmb / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

async function resizeImage(dataUri, maxDim) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (Math.max(width, height) > maxDim) {
        const ratio = maxDim / Math.max(width, height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => resolve(dataUri);
    img.src = dataUri;
  });
}
